import React, { useState } from 'react';
import { Layout, Menu, Typography, Space, Image, Button, Modal, Form, message, Input } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  LogoutOutlined,
  FileTextOutlined,
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ArrowLeftOutlined,
  LinkOutlined,
  BookOutlined,
  AppstoreOutlined,
  VideoCameraOutlined,
  SettingOutlined,
  DownloadOutlined,
  FileSearchOutlined,
  ClusterOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../services/axiosInstance';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const AppLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [apiKeyForm] = Form.useForm();
  const [changePasswordModalVisible, setChangePasswordModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // 获取问候语
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 23 || hour < 5) {
      return '夜深了，记得早点休息哦！';
    } else if (hour < 11) {
      return '早上好，';
    } else if (hour < 14) {
      return '中午好，';
    } else if (hour < 18) {
      return '下午好，';
    } else {
      return '晚上好，';
    }
  };

  // 创建常规菜单项
  const regularMenuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: '首页'
    },
    {
      key: '/profile',
      icon: <UserOutlined />,
      label: '个人信息'
    },
    {
      key: '/pdf/files',
      icon: <FileTextOutlined />,
      label: '文档解析'
    },
    {
      key: '/match',
      icon: <LinkOutlined />,
      label: '题答匹配'
    },
    {
      key: '/knowledge',
      icon: <ClusterOutlined />,
      label: '知识管理'
    },
    {
      key: '/video-audit/task-list',
      icon: <VideoCameraOutlined />,
      label: '网课审核'
    },
    {
      key: '/video-download',
      icon: <DownloadOutlined />,
      label: '网课存档'
    },
    {
      key: '/live-sim',
      icon: <VideoCameraOutlined />,
      label: '模拟直播'
    },
    {
      key: '/questions',
      icon: <QuestionCircleOutlined />,
      label: '题库管理'
    }
  ];
  
  // 创建管理员菜单项
  const adminMenuItems = user?.user_group === 'admin' ? [
    {
      type: 'divider'
    },
    {
      key: '/users',
      icon: <TeamOutlined />,
      label: '用户管理',
      style: { marginTop: '8px' }
    },
    {
      key: '/download-logs',
      icon: <FileSearchOutlined />,
      label: '下载日志',
      style: { marginTop: '8px' }
    },
    {
      key: 'questions',
      icon: <QuestionCircleOutlined />,
      label: '题库维护',
      children: [
        {
          key: '/questions/types',
          label: '题目类型'
        },
        {
          key: '/questions/tags',
          label: '题目标签'
        }
      ]
    }
  ] : [];
  
  // 合并菜单项
  const menuItems = [...regularMenuItems, ...adminMenuItems];

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 判断是否是编辑页面
  const isEditPage = location.pathname.startsWith('/pdf/edit/') || location.pathname.startsWith('/match/edit/');

  // 获取当前页面标题
  const getPageTitle = () => {
    if (location.pathname.startsWith('/pdf/edit/')) {
      return '文档编辑';
    } else if (location.pathname.startsWith('/match/edit/')) {
      return '匹配编辑';
    } else if (location.pathname === '/match/create') {
      return '创建匹配';
    } else if (location.pathname === '/pdf/files') {
      return '文档解析';
    } else if (location.pathname === '/match') {
      return '题答匹配';
    } else if (location.pathname === '/profile') {
      return '个人信息';
    } else if (location.pathname === '/users') {
      return '用户管理';
    } else if (location.pathname === '/knowledge') {
      return '知识管理';
    } else if (location.pathname === '/video-audit/task-list') {
      return '网课审核';
    } else if (location.pathname.startsWith('/video-audit/detail/')) {
      return '审核详情';
    } else if (location.pathname === '/video-download') {
      return '网课存档';
    } else if (location.pathname === '/live-sim') {
      return '模拟直播';
    } else {
      return '首页';
    }
  };

  // 判断是否显示返回按钮
  const showBackButton = () => {
    if (location.pathname.startsWith('/pdf/edit/')) {
      return '/pdf/files';
    } else if (location.pathname.startsWith('/match/edit/') || location.pathname === '/match/create') {
      return '/match';
    } else if (location.pathname.startsWith('/video-audit/detail/')) {
      return '/video-audit/task-list';
    }
    return null;
  };

  // 打开设置Modal
  const showSettings = () => {
    apiKeyForm.setFieldsValue({});
    setSettingsVisible(true);
  };

  // 保存设置
  const saveSettings = () => {
    apiKeyForm.validateFields()
      .then(values => {
        message.success('设置已保存');
        setSettingsVisible(false);
      })
      .catch(info => {
        console.log('验证失败:', info);
      });
  };

  // 打开修改密码对话框
  const showChangePasswordModal = () => {
    setSettingsVisible(false);
    passwordForm.resetFields();
    setChangePasswordModalVisible(true);
  };

  // 处理密码修改
  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      
      // 调用修改密码API
      await axiosInstance.post('auth/change-password', {
        old_password: values.oldPassword,
        new_password: values.newPassword
      });
      
      message.success('密码修改成功');
      setChangePasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (err) {
      console.error('修改密码失败:', err);
      message.error('密码修改失败: ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        collapsible 
        collapsed={collapsed} 
        onCollapse={setCollapsed}
        theme="light"
        width={180}
        collapsedWidth={48}
        breakpoint="lg"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'sticky',
          top: 0,
          left: 0
        }}
      >
        <div style={{ 
          height: 48, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: '#004d40',
          padding: '8px'
        }}>
          <Image
            src={collapsed ? '/logo-mini.png' : '/logo-wide.png'}
            alt="Logo"
            preview={false}
            style={{ 
              height: '32px',
              objectFit: 'contain'
            }}
          />
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout>
        <Header style={{ 
          background: '#fff', 
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Space>
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              onClick: () => setCollapsed(!collapsed),
              style: { fontSize: '16px', cursor: 'pointer' }
            })}
            {showBackButton() && (
              <Button 
                type="primary" 
                size="small"
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate(showBackButton())}
                style={{ 
                  marginRight: '12px',
                  backgroundColor: '#004d40',
                  borderColor: '#004d40',
                  borderRadius: '4px'
                }}
              >
                返回列表
              </Button>
            )}
            <Title level={4} style={{ margin: 0 }}>
              {getPageTitle()}
            </Title>
            {isEditPage && (
              <div style={{ 
                marginLeft: '16px', 
                fontSize: '14px', 
                color: '#00695c',
                padding: '4px 10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span role="img" aria-label="提示" style={{ marginRight: '6px' }}>💡</span>
                <span>请与原始文件比对内容，检查是否有遗漏，并整理好大纲结构。关闭页面前请务必保存修改。</span>
              </div>
            )}
            {location.pathname.startsWith('/pdf/edit/') && (
              <Button 
                type="primary" 
                size="small"
                icon={<i className="anticon" style={{ 
                  backgroundImage: "url('data:image/svg+xml;utf8,<svg viewBox=\"0 0 1024 1024\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" width=\"16\" height=\"16\"><path d=\"M888.32 792.576v63.488h-752.64v-63.488h752.64z m0-99.328v63.488h-752.64v-63.488h752.64z m-564.224-300.032h-62.464v335.872h62.464v-335.872z m188.416-188.416h-62.464v524.288h62.464v-524.288z m188.928 106.496h-62.464v417.792h62.464v-417.792z\" fill=\"white\"></path></svg>')",
                  width: '16px',
                  height: '16px',
                  backgroundSize: 'cover',
                  display: 'inline-block',
                  marginRight: '4px',
                  verticalAlign: 'text-bottom'
                }}></i>}
                onClick={() => {
                  // 调用全局统计方法
                  if (window.vditorRef && window.vditorRef.current) {
                    // 首先保存
                    const onSave = window.onSaveCallback;
                    if (onSave) {
                      try {
                        const content = window.vditorRef.current.getValue();
                        onSave(content);
                        // 然后触发统计
                        if (typeof window.handleContentStats === 'function') {
                          window.handleContentStats();
                        }
                      } catch (error) {
                        console.error('统计时出错:', error);
                      }
                    }
                  }
                }}
                style={{ 
                  marginLeft: '12px',
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff',
                  borderRadius: '4px'
                }}
              >
                保存并查看结果
              </Button>
            )}
          </Space>
          <div>
            <Space>
              <span style={{ marginRight: '16px' }}>
                {getGreeting()}
                <span style={{ fontWeight: 'bold' }}>{user?.fullname || user?.username}</span>
              </span>
              <Button 
                type="text" 
                icon={<SettingOutlined />} 
                onClick={showSettings}
                style={{ marginRight: '8px' }}
              />
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                style={{ color: '#ff4d4f' }}
              >
                退出登录
              </Button>
            </Space>
          </div>
        </Header>
        <Content style={{
          margin: isEditPage ? '0' : '10px 10px', 
          padding: isEditPage ? 0 : 24, 
          background: '#fff',
          minHeight: 280,
          overflow: isEditPage ? 'hidden' : 'visible'
        }}>
          <Outlet />
        </Content>
      </Layout>

      {/* 系统设置对话框 */}
      <Modal
        title="系统设置"
        open={settingsVisible}
        onOk={saveSettings}
        onCancel={() => setSettingsVisible(false)}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={apiKeyForm}
          layout="vertical"
        >
          <div style={{ marginBottom: '20px' }}>
            <Typography.Text type="secondary">
              系统设置功能待开发...
            </Typography.Text>
          </div>
          
          <Button 
            type="primary" 
            onClick={showChangePasswordModal}
            style={{ width: '100%' }}
          >
            修改密码
          </Button>
        </Form>
      </Modal>
      
      {/* 修改密码对话框 */}
      <Modal
        title="修改密码"
        open={changePasswordModalVisible}
        onOk={handleChangePassword}
        onCancel={() => setChangePasswordModalVisible(false)}
        okText="修改"
        cancelText="取消"
      >
        <Form
          form={passwordForm}
          layout="vertical"
        >
          <Form.Item
            name="oldPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>
          
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度不能少于6个字符' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请确认新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AppLayout; 